import { Link } from "react-router-dom";
import { GraduationCap, Users, School, BookOpen } from "lucide-react";

const FEATURED_CLASS = {
  title: "The Ones Who Remembered",
  volume: "For the Public Vol. 01 / Issue 01",
  cover: "https://customer-assets-39nsmqrw.emergentagent.net/job_archive-democracy/artifacts/738316034edb9bf3_Screenshot%202026-10-15%20at%2010.24.16.png",
  formUrl: "https://form.typeform.com/to/F7gIX0PC",
};

const TRACKS = [
  { code: "A", title: "For Young Learners", Icon: School, desc: "Foundations of civic literacy, storytelling, and inquiry designed for K–12 age groups. Curriculum shaped with teachers and community mentors.", featured: FEATURED_CLASS },
  { code: "B", title: "For University Cohorts", Icon: BookOpen, desc: "Advanced methods in social research, decolonial theory, and applied policy. Semester-long tracks with visiting scholars." },
  { code: "C", title: "For General Citizens", Icon: Users, desc: "Open-enrollment evening and weekend classes. No prerequisites, no age limit. Delivered online and in real community spaces." },
];

export default function Courses() {
  return (
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 md:py-24">
      <div className="flex items-center gap-4 mb-8">
        <GraduationCap size={30} strokeWidth={1.25} />
        <div className="eyebrow">Courses</div>
        <span className="font-ui text-[10px] tracking-[0.25em] uppercase text-[#6C6C6C] border border-[#DCDCDC] px-2 py-1">Coming Soon · TBA</span>
      </div>

      <h1 className="font-headline text-5xl md:text-6xl lg:text-7xl leading-none tracking-tight max-w-4xl">
        Classes for every age. In an academic space, or in real life.
      </h1>

      <p className="font-body text-lg md:text-xl text-[#3A3A3A] mt-8 max-w-3xl leading-relaxed">
        A rolling catalog of open classes designed so anyone can pick up and learn, regardless of age. Some tracks are shaped for specific age groups. Many are for the general citizen, delivered online or in real spaces near you. Curriculum, cohort dates, and instructors will be announced.
      </p>

      <div className="mt-16 grid grid-cols-1 md:grid-cols-3 border-t border-l border-[#DCDCDC]" data-testid="course-tracks">
        {TRACKS.map((c) => (
          <div key={c.code} className="border-r border-b border-[#DCDCDC] p-8">
            <div className="flex items-baseline justify-between mb-6">
              <div className="num text-[#DCDCDC]">{c.code}</div>
              <c.Icon size={26} strokeWidth={1.25} />
            </div>
            <div className="font-ui text-[10px] tracking-[0.3em] uppercase text-[#6C6C6C]">Track {c.code}</div>
            <h3 className="font-headline text-2xl md:text-3xl mt-3 leading-tight">{c.title}</h3>
            <p className="font-body text-[#3A3A3A] mt-4">{c.desc}</p>
            {c.featured ? (
              <div className="mt-8 border border-[#DCDCDC]" data-testid="track-a-featured-class">
                <a href={c.featured.formUrl} target="_blank" rel="noopener noreferrer" aria-label={`Enroll in ${c.featured.title}`}>
                  <img
                    src={c.featured.cover}
                    alt={`${c.featured.title} — class flyer`}
                    className="w-full block hover:opacity-90 transition-opacity"
                    loading="lazy"
                    data-testid="featured-class-cover"
                  />
                </a>
                <div className="p-5 border-t border-[#DCDCDC]">
                  <div className="font-ui text-[9px] tracking-[0.25em] uppercase text-[#6C6C6C]">Now Enrolling · {c.featured.volume}</div>
                  <div className="font-headline text-xl mt-2 leading-tight">{c.featured.title}</div>
                  <a
                    href={c.featured.formUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-primary mt-5 inline-block"
                    data-testid="featured-class-enroll-btn"
                  >
                    Enroll Now
                  </a>
                </div>
              </div>
            ) : (
              <div className="mt-8 font-ui text-[9px] tracking-[0.25em] uppercase text-[#6C6C6C]">Announced · TBA</div>
            )}
          </div>
        ))}
      </div>

      <hr className="hairline my-16" />

      <div className="border border-[#DCDCDC] p-10 md:p-14 max-w-4xl mx-auto text-center">
        <div className="eyebrow">Register Interest</div>
        <div className="font-headline text-3xl md:text-4xl mt-4">
          Be the first to know when a cohort opens.
        </div>
        <p className="font-body text-[#3A3A3A] mt-4 max-w-2xl mx-auto">
          Leave a note through Get Involved and we'll write to you as soon as the first course opens for enrollment.
        </p>
        <div className="mt-8">
          <Link to="/get-involved" className="btn-primary" data-testid="courses-notify-cta">Notify Me</Link>
        </div>
      </div>
    </div>
  );
}
